import { useState } from "react";
import { BadgeCheck, Cloud, Monitor, Shield, Users } from "lucide-react";
import { MmcFrame } from "../../admin-framework/MmcFrame.js";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

type CloudDraft = Record<string, string>;

interface CloudAction {
  buildPayload: (draft: CloudDraft) => Record<string, unknown>;
  capability: string;
  commandType: string;
  label: string;
}

interface CloudSection {
  actions: CloudAction[];
  columns: Array<{ key: string; label: string }>;
  defaults: CloudDraft;
  description: string;
  rows: Array<{ id: string; values: Record<string, string> }>;
  title: string;
}

const fieldLabels: Record<string, string> = {
  action: "Action",
  appName: "Application",
  deviceId: "Device",
  displayName: "Display name",
  enabled: "Enabled",
  groupName: "Group",
  incidentId: "Incident",
  licenseSku: "License",
  method: "Method",
  ownerUpn: "Owner",
  policyName: "Policy",
  profileName: "Profile",
  quantity: "Quantity",
  roleName: "Role",
  scope: "Consent scope",
  target: "Target",
  trigger: "Trigger",
  type: "Type",
  upn: "User"
};

const cloudSections: Record<string, CloudSection> = {
  authMethods: {
    actions: [
      {
        buildPayload: (draft) => ({ method: draft.method }),
        capability: "cloud.entra.auth.write",
        commandType: "AUTH_METHOD_ENABLE",
        label: "Enable method"
      },
      {
        buildPayload: (draft) => ({ method: draft.method, upn: draft.upn }),
        capability: "cloud.entra.auth.write",
        commandType: "AUTH_METHOD_REGISTER_FOR_USER",
        label: "Register for user"
      }
    ],
    columns: [{ key: "method", label: "Method" }, { key: "state", label: "State" }, { key: "scope", label: "Scope" }],
    defaults: { method: "mfa", upn: "alex.student@lab.local" },
    description: "Manage cloud authentication methods and per-user registrations.",
    rows: [
      { id: "mfa", values: { method: "MFA", state: "Enabled", scope: "All users" } },
      { id: "passwordless", values: { method: "Passwordless", state: "Available", scope: "Pilot" } }
    ],
    title: "Authentication Methods"
  },
  compliancePolicies: {
    actions: [
      {
        buildPayload: (draft) => ({ policyName: draft.policyName }),
        capability: "cloud.intune.compliance.write",
        commandType: "COMPLIANCE_POLICY_CREATE",
        label: "Create policy"
      },
      {
        buildPayload: (draft) => ({ policyName: draft.policyName, target: draft.target }),
        capability: "cloud.intune.compliance.write",
        commandType: "COMPLIANCE_POLICY_ASSIGN",
        label: "Assign policy"
      }
    ],
    columns: [{ key: "policy", label: "Policy" }, { key: "platform", label: "Platform" }, { key: "assignment", label: "Assignment" }],
    defaults: { policyName: "FD-Compliant-Baseline", target: "Helpdesk Devices" },
    description: "Create and assign device compliance policies.",
    rows: [
      { id: "baseline", values: { assignment: "Helpdesk Devices", platform: "Workstation", policy: "FD-Compliant-Baseline" } },
      { id: "servers", values: { assignment: "Server Admins", platform: "Server", policy: "Server Access Baseline" } }
    ],
    title: "Compliance Policies"
  },
  conditionalAccess: {
    actions: [
      {
        buildPayload: (draft) => ({ enabled: draft.enabled === "true", policyName: draft.policyName }),
        capability: "cloud.entra.policy.write",
        commandType: "CA_POLICY_CREATE",
        label: "Create policy"
      },
      {
        buildPayload: (draft) => ({ enabled: draft.enabled === "true", policyName: draft.policyName }),
        capability: "cloud.entra.policy.write",
        commandType: "CA_POLICY_TOGGLE",
        label: "Toggle policy"
      }
    ],
    columns: [{ key: "policy", label: "Policy" }, { key: "state", label: "State" }, { key: "target", label: "Target" }],
    defaults: { enabled: "true", policyName: "Require MFA for admins" },
    description: "Create and enable sign-in access policies.",
    rows: [
      { id: "mfa-admins", values: { policy: "Require MFA for admins", state: "Enabled", target: "Privileged roles" } },
      { id: "block-legacy", values: { policy: "Block legacy auth", state: "Report-only", target: "All users" } }
    ],
    title: "Access Policies"
  },
  defenderPortal: {
    actions: [
      {
        buildPayload: (draft) => ({ incidentId: draft.incidentId }),
        capability: "cloud.defender.write",
        commandType: "DEFENDER_INCIDENT_RESOLVE",
        label: "Resolve incident"
      },
      {
        buildPayload: (draft) => ({ incidentId: draft.incidentId }),
        capability: "cloud.defender.write",
        commandType: "DEFENDER_ALERT_INVESTIGATE",
        label: "Investigate alert"
      }
    ],
    columns: [{ key: "incident", label: "Incident" }, { key: "severity", label: "Severity" }, { key: "state", label: "State" }],
    defaults: { incidentId: "INC-2026-001" },
    description: "Track and close cloud security incidents.",
    rows: [
      { id: "inc1", values: { incident: "INC-2026-001", severity: "High", state: "Active" } },
      { id: "inc2", values: { incident: "INC-2026-002", severity: "Medium", state: "Investigating" } }
    ],
    title: "Security Portal"
  },
  enterpriseApps: {
    actions: [
      {
        buildPayload: (draft) => ({ appName: draft.appName }),
        capability: "cloud.entra.app.write",
        commandType: "ENTERPRISE_APP_REGISTER",
        label: "Register app"
      },
      {
        buildPayload: (draft) => ({ appName: draft.appName, scope: draft.scope }),
        capability: "cloud.entra.app.write",
        commandType: "ENTERPRISE_APP_GRANT_CONSENT",
        label: "Grant consent"
      }
    ],
    columns: [{ key: "app", label: "Application" }, { key: "publisher", label: "Publisher" }, { key: "consent", label: "Consent" }],
    defaults: { appName: "FD Ticket Helper", scope: "User.Read" },
    description: "Register cloud applications and record consent decisions.",
    rows: [
      { id: "helper", values: { app: "FD Ticket Helper", consent: "User.Read", publisher: "FirstDesk Lab" } },
      { id: "reporting", values: { app: "Reporting Connector", consent: "Pending", publisher: "Training Tenant" } }
    ],
    title: "Enterprise Applications"
  },
  entraDevices: {
    actions: [
      {
        buildPayload: (draft) => ({ deviceId: draft.deviceId, displayName: draft.displayName }),
        capability: "cloud.entra.device.write",
        commandType: "ENTRA_DEVICE_REGISTER",
        label: "Register device"
      },
      {
        buildPayload: (draft) => ({ deviceId: draft.deviceId }),
        capability: "cloud.entra.device.write",
        commandType: "ENTRA_DEVICE_REVOKE",
        label: "Revoke device"
      }
    ],
    columns: [{ key: "device", label: "Device" }, { key: "owner", label: "Owner" }, { key: "state", label: "State" }],
    defaults: { deviceId: "CLD-WS-152", displayName: "Cloud Workstation 152" },
    description: "Register and revoke cloud directory devices.",
    rows: [
      { id: "cld1", values: { device: "CLD-WS-152", owner: "alex.student@lab.local", state: "Registered" } },
      { id: "cld2", values: { device: "CLD-LT-201", owner: "sam.admin@lab.local", state: "Registered" } }
    ],
    title: "Cloud Devices"
  },
  entraGroups: {
    actions: [
      {
        buildPayload: (draft) => ({ groupName: draft.groupName, type: draft.type }),
        capability: "cloud.entra.group.write",
        commandType: "ENTRA_GROUP_CREATE",
        label: "Create group"
      },
      {
        buildPayload: (draft) => ({ groupName: draft.groupName, upn: draft.upn }),
        capability: "cloud.entra.group.write",
        commandType: "ENTRA_GROUP_ADD_MEMBER",
        label: "Add member"
      }
    ],
    columns: [{ key: "group", label: "Group" }, { key: "type", label: "Type" }, { key: "members", label: "Members" }],
    defaults: { groupName: "Helpdesk Trainees", type: "security", upn: "alex.student@lab.local" },
    description: "Create cloud groups and manage membership.",
    rows: [
      { id: "trainees", values: { group: "Helpdesk Trainees", members: "12", type: "Security" } },
      { id: "admins", values: { group: "Cloud Admins", members: "3", type: "Security" } }
    ],
    title: "Cloud Groups"
  },
  entraRoles: {
    actions: [
      {
        buildPayload: (draft) => ({ roleName: draft.roleName, upn: draft.upn }),
        capability: "cloud.entra.role.write",
        commandType: "ENTRA_ROLE_ASSIGN",
        label: "Assign role"
      },
      {
        buildPayload: (draft) => ({ roleName: draft.roleName, upn: draft.upn }),
        capability: "cloud.entra.role.write",
        commandType: "ENTRA_ROLE_REMOVE",
        label: "Remove role"
      }
    ],
    columns: [{ key: "role", label: "Role" }, { key: "assignee", label: "Assignee" }, { key: "state", label: "State" }],
    defaults: { roleName: "Cloud Administrator", upn: "sam.admin@lab.local" },
    description: "Assign and remove cloud admin roles.",
    rows: [
      { id: "cloud-admin", values: { assignee: "sam.admin@lab.local", role: "Cloud Administrator", state: "Eligible" } },
      { id: "reader", values: { assignee: "alex.student@lab.local", role: "Reports Reader", state: "Active" } }
    ],
    title: "Cloud Roles"
  },
  entraUsers: {
    actions: [
      {
        buildPayload: (draft) => ({ displayName: draft.displayName, upn: draft.upn }),
        capability: "cloud.entra.user.write",
        commandType: "ENTRA_USER_CREATE",
        label: "Create user"
      },
      {
        buildPayload: (draft) => ({ upn: draft.upn }),
        capability: "cloud.entra.user.write",
        commandType: "ENTRA_USER_RESET_PASSWORD",
        label: "Reset password"
      },
      {
        buildPayload: (draft) => ({ upn: draft.upn }),
        capability: "cloud.entra.user.write",
        commandType: "ENTRA_USER_DELETE",
        label: "Delete user"
      }
    ],
    columns: [{ key: "user", label: "User" }, { key: "upn", label: "Sign-in" }, { key: "state", label: "State" }],
    defaults: { displayName: "Alex Student", upn: "alex.student@lab.local" },
    description: "Create users, reset passwords and remove accounts in the cloud directory.",
    rows: [
      { id: "alex", values: { state: "Enabled", upn: "alex.student@lab.local", user: "Alex Student" } },
      { id: "sam", values: { state: "Enabled", upn: "sam.admin@lab.local", user: "Sam Admin" } }
    ],
    title: "Cloud Users"
  },
  intuneDevices: {
    actions: [
      {
        buildPayload: (draft) => ({ deviceId: draft.deviceId, ownerUpn: draft.ownerUpn }),
        capability: "cloud.intune.device.write",
        commandType: "INTUNE_DEVICE_ENROLL",
        label: "Enroll device"
      },
      {
        buildPayload: (draft) => ({ deviceId: draft.deviceId }),
        capability: "cloud.intune.device.write",
        commandType: "INTUNE_DEVICE_RETIRE",
        label: "Retire device"
      }
    ],
    columns: [{ key: "device", label: "Device" }, { key: "owner", label: "Owner" }, { key: "state", label: "State" }],
    defaults: { deviceId: "MDM-001", ownerUpn: "alex.student@lab.local" },
    description: "Enroll and retire managed cloud devices.",
    rows: [
      { id: "mdm1", values: { device: "MDM-001", owner: "alex.student@lab.local", state: "Compliant" } },
      { id: "mdm2", values: { device: "MDM-002", owner: "sam.admin@lab.local", state: "Needs attention" } }
    ],
    title: "Managed Devices"
  },
  m365Licenses: {
    actions: [
      {
        buildPayload: (draft) => ({ licenseSku: draft.licenseSku, quantity: Number(draft.quantity || 1) }),
        capability: "cloud.m365.license.write",
        commandType: "M365_LICENSE_PURCHASE",
        label: "Add licenses"
      },
      {
        buildPayload: (draft) => ({ licenseSku: draft.licenseSku }),
        capability: "cloud.m365.license.write",
        commandType: "M365_LICENSE_REVOKE",
        label: "Revoke licenses"
      }
    ],
    columns: [{ key: "sku", label: "License" }, { key: "available", label: "Available" }, { key: "assigned", label: "Assigned" }],
    defaults: { licenseSku: "FD-Productivity-E3", quantity: "5" },
    description: "Add and revoke cloud license capacity.",
    rows: [
      { id: "prod", values: { assigned: "24", available: "6", sku: "FD-Productivity-E3" } },
      { id: "security", values: { assigned: "8", available: "2", sku: "FD-Security-Plan" } }
    ],
    title: "Cloud Licenses"
  },
  m365Users: {
    actions: [
      {
        buildPayload: (draft) => ({ licenseSku: draft.licenseSku, upn: draft.upn }),
        capability: "cloud.m365.user.write",
        commandType: "M365_USER_LICENSE_ASSIGN",
        label: "Assign license"
      }
    ],
    columns: [{ key: "user", label: "User" }, { key: "license", label: "License" }, { key: "state", label: "State" }],
    defaults: { licenseSku: "FD-Productivity-E3", upn: "alex.student@lab.local" },
    description: "Assign cloud licenses to users.",
    rows: [
      { id: "alex", values: { license: "FD-Productivity-E3", state: "Assigned", user: "alex.student@lab.local" } },
      { id: "sam", values: { license: "FD-Security-Plan", state: "Assigned", user: "sam.admin@lab.local" } }
    ],
    title: "Cloud Licensed Users"
  },
  configurationProfiles: {
    actions: [
      {
        buildPayload: (draft) => ({ profileName: draft.profileName }),
        capability: "cloud.intune.config.write",
        commandType: "CONFIG_PROFILE_CREATE",
        label: "Create profile"
      },
      {
        buildPayload: (draft) => ({ profileName: draft.profileName, target: draft.target }),
        capability: "cloud.intune.config.write",
        commandType: "CONFIG_PROFILE_ASSIGN",
        label: "Assign profile"
      }
    ],
    columns: [{ key: "profile", label: "Profile" }, { key: "platform", label: "Platform" }, { key: "assignment", label: "Assignment" }],
    defaults: { profileName: "FD Browser Baseline", target: "Helpdesk Devices" },
    description: "Create and assign device configuration profiles.",
    rows: [
      { id: "browser", values: { assignment: "Helpdesk Devices", platform: "Workstation", profile: "FD Browser Baseline" } },
      { id: "vpn", values: { assignment: "Remote Users", platform: "Workstation", profile: "VPN Baseline" } }
    ],
    title: "Configuration Profiles"
  }
};

const navItems = [
  ["entraUsers", "Cloud Users", Users],
  ["entraGroups", "Cloud Groups", Users],
  ["entraDevices", "Cloud Devices", Monitor],
  ["entraRoles", "Cloud Roles", BadgeCheck],
  ["conditionalAccess", "Access Policies", Shield],
  ["authMethods", "Authentication", Shield],
  ["enterpriseApps", "Applications", Cloud],
  ["intuneDevices", "Managed Devices", Monitor],
  ["compliancePolicies", "Compliance", Shield],
  ["configurationProfiles", "Config Profiles", Cloud],
  ["m365Users", "Licensed Users", Users],
  ["m365Licenses", "Licenses", BadgeCheck],
  ["defenderPortal", "Security Portal", Shield]
] as const;

export function CloudAdminApp({ activeMachine, app, onOpenApp, onRunCommand }: SimulatorAppSurfaceProps) {
  const section = cloudSections[app.id] ?? cloudSections.entraUsers;
  const [draft, setDraft] = useState<CloudDraft>(() => ({ ...section.defaults }));

  async function runAction(action: CloudAction) {
    await onRunCommand({
      appId: app.id,
      commandType: action.commandType,
      capability: action.capability,
      label: action.label,
      payload: {
        machineId: activeMachine,
        ...action.buildPayload(draft)
      }
    });
  }

  return (
    <div className="fd-sim-app fd-cloud-admin">
      <MmcFrame
        actions={section.actions.map((action) => ({
          label: action.label,
          onClick: () => void runAction(action)
        }))}
        columns={section.columns}
        rows={section.rows}
        title={section.title}
        tree={navItems.map(([id, label, Icon]) => ({
          active: id === app.id,
          icon: <Icon size={15} />,
          id,
          label,
          onClick: () => onOpenApp(id, "start", { surface: "cloud-admin" })
        }))}
      >
        <section className="fd-cloud-summary">
          <div>
            <p>FirstDesk Cloud Admin Center</p>
            <h3>{section.title}</h3>
            <span>{section.description}</span>
          </div>
          <strong>{activeMachine}</strong>
        </section>
        <section className="fd-mmc-detail-panel">
          <header>
            <div>
              <p>Command payload</p>
              <h3>{section.actions[0]?.label ?? "Action"}</h3>
            </div>
          </header>
          <div className="fd-command-form fd-cloud-command-form">
            {Object.entries(draft).map(([key, value]) => (
              <label key={key}>
                {fieldLabels[key] ?? key}
                <input value={value} onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))} />
              </label>
            ))}
          </div>
          <div className="fd-cloud-action-row">
            {section.actions.map((action) => (
              <button key={action.commandType} type="button" onClick={() => void runAction(action)}>
                {action.label}
              </button>
            ))}
          </div>
        </section>
      </MmcFrame>
    </div>
  );
}
